import { Dispatch, SetStateAction } from 'react'
import { FormFieldsEdit, WellUseFieldsEdit } from '../../../../types/form-types'
import { Permissions } from '../../../../types/user-types'
import { useWellState } from '../../../context/well-context'
import { AddField, RemoveField, ValidationErrors, ValidationErrorsArray } from '../../../hooks/use-edit-form'
import useUser from '../../../hooks/use-user'
import { RequiredText, WellStatus } from '../../../services/constants'
import { can } from '../../../services/user-utils'
import LookupField from '../../wells/lookup-field'
import ReadOnlyLookupField from '../../wells/readonly-lookup-field'
import { WellDetailsComponentBase } from './well-details-types'
import { FormField, Heading, ReadOnlyField, RepeatableField, TextInput, TypeaheadOption } from '@msaf/core-react'

interface WellDetailsIdentifiersEditProps extends Omit<WellDetailsComponentBase, 'validationErrors'> {
  addField: AddField<FormFieldsEdit>
  removeField: RemoveField<FormFieldsEdit>
  newWellCouncilId?: string
  setIsExistingOrDecommissioned?: Dispatch<SetStateAction<boolean>>
  setIsWellNotDrilled?: Dispatch<SetStateAction<boolean>>
  validationErrors: ValidationErrors & ValidationErrorsArray
}

export default function WellDetailsIdentifiersEdit({
  isLoading,
  fields,
  validationErrors,
  isSetupRecord,
  setValue,
  addField,
  removeField,
  newWellCouncilId,
  setIsExistingOrDecommissioned,
  setIsWellNotDrilled,
}: WellDetailsIdentifiersEditProps) {
  const { user } = useUser()
  const {
    wellState: { isLegacyWell, isPublished },
  } = useWellState()

  const handleWellStatus = ({ label }: TypeaheadOption) => {
    if (setIsExistingOrDecommissioned) {
      setIsExistingOrDecommissioned(label === WellStatus.EXISTING || label === WellStatus.DECOMMISSIONED)
    }

    if (setIsWellNotDrilled) {
      setIsWellNotDrilled(label === WellStatus.NOT_DRILLED)
    }
  }

  const getCouncilLookupValue = () => {
    if (newWellCouncilId) return newWellCouncilId
    if (fields.council) return String(fields.council)
    if (user?.councilId) return String(user.councilId)
    return ''
  }

  return (
    <>
      <Heading isSkeleton={isLoading} level={3}>
        Identifiers
      </Heading>

      {/* New Well Record Setup - if user is sysAdmin  show a lookup field for region. */}
      {isSetupRecord && can(user, Permissions.MAINTAIN_ALL_USERS) ? (
        <FormField
          isSkeleton={isLoading}
          label='Region'
          labelId='wellRegionLabelId'
          validationMessages={validationErrors?.council}
          isRequired
        >
          <LookupField
            isSkeleton={isLoading}
            labelledBy='wellRegionLabelId'
            fieldId='listCouncils'
            value={getCouncilLookupValue()}
            setValue={({ value }) => setValue('council', value)}
            isInvalid={!!validationErrors?.council}
          />
        </FormField>
      ) : (
        // New Well Record setup - if user is not sysAdmin show a readonly field to display the councilRegionName for the council.
        <FormField isSkeleton={isLoading} label='Region' labelId='wellRegionLabelId'>
          <ReadOnlyLookupField
            fieldId='listCouncils'
            isSkeleton={isLoading}
            labelledBy='wellRegionLabelId'
            value={getCouncilLookupValue()}
          />
        </FormField>
      )}

      {isPublished ? (
        <FormField isSkeleton={isLoading} labelId='councilWellNo' label='Council well number'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='councilWellNo' value={`${fields.councilWellNo ?? ''}`} />
        </FormField>
      ) : (
        <FormField
          isSkeleton={isLoading}
          label='Council well number'
          htmlFor='councilWellNo'
          validationMessages={validationErrors?.councilWellNo}
          isRequired
        >
          <TextInput
            isSkeleton={isLoading}
            id='councilWellNo'
            value={fields.councilWellNo}
            setValue={(value) => setValue('councilWellNo', value)}
            isInvalid={!!validationErrors?.councilWellNo}
          />
        </FormField>
      )}

      <FormField
        isSkeleton={isLoading}
        label='Status'
        labelId='status'
        validationMessages={validationErrors?.wellStatus}
        isRequired={!isLegacyWell}
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy={'status'}
          fieldId='wellStatus'
          value={fields.wellStatus}
          setValue={({ value }) => setValue('wellStatus', value)}
          handleChangeCallback={handleWellStatus}
          isInvalid={!!validationErrors?.wellStatus}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Well name'
        htmlFor='wellName'
        validationMessages={validationErrors?.wellName}
      >
        <TextInput
          isSkeleton={isLoading}
          id='wellName'
          value={fields.wellName}
          setValue={(value) => setValue('wellName', value)}
          isInvalid={!!validationErrors?.wellName}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Type'
        labelId='type'
        validationMessages={validationErrors?.wellType}
        isRequired={!isLegacyWell}
      >
        <LookupField
          isSkeleton={isLoading}
          labelledBy={'type'}
          fieldId='wellType'
          value={fields.wellType}
          setValue={({ value }) => setValue('wellType', value)}
          isInvalid={!!validationErrors?.wellType}
        />
      </FormField>
      <RepeatableField
        isSkeleton={isLoading}
        label='Well use(s)'
        labelId='well-use-group'
        removeField={(index: number) => removeField('wellUses', index)}
        addField={() => addField('wellUses', {})}
        isRequired={!isLegacyWell}
        // TOTO Repeatable fields may require asterixk to be set by default
        requiredText={RequiredText.ASTERISK}
        components={fields.wellUses?.map((field: Partial<WellUseFieldsEdit>, idx) => (
          <LookupField
            isSkeleton={isLoading}
            key={field.id}
            labelledBy='well-use-group'
            fieldId='wellUse'
            value={field.use}
            setValue={({ value }) => setValue('wellUses', value, 'use', idx)}
            isInvalid={!!validationErrors?.wellUses?.[idx]}
            validationMessages={
              validationErrors?.wellUses?.[idx] && 'use' in validationErrors?.wellUses?.[idx]
                ? validationErrors?.wellUses?.[idx]?.['use' as keyof typeof validationErrors]
                : undefined
            }
          />
        ))}
      />
    </>
  )
}
